import { DestroyRef, Injectable, effect, inject, signal } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';
import { Subject, catchError, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { NotificationsService } from '../api/notifications.service';
import { AuthService } from '../auth/auth.service';
import { AppNotification } from '../models/notification.models';

/**
 * The live notification channel. The backend pushes one user's new notifications and authoritative unread count;
 * the bell falls back to its timer only while this connection is down.
 */
@Injectable({ providedIn: 'root' })
export class NotificationHubService {
  private readonly auth = inject(AuthService);
  private readonly notifications = inject(NotificationsService);
  private readonly destroyRef = inject(DestroyRef);

  private connection: HubConnection | null = null;
  private connectionToken: string | null = null;

  readonly connected = signal(false);

  private readonly notificationSubject = new Subject<AppNotification>();
  private readonly unreadCountSubject = new Subject<number>();

  readonly notification$ = this.notificationSubject.asObservable();
  readonly unreadCount$ = this.unreadCountSubject.asObservable();

  constructor() {
    // Signing in (or switching user) connects; signing out disconnects.
    effect(() => {
      const token = this.auth.accessToken();
      if (!token || !this.auth.isAuthenticated()) {
        void this.stop();
        return;
      }
      void this.start(token);
    });

    this.destroyRef.onDestroy(() => void this.stop());
  }

  private async start(token: string): Promise<void> {
    if (this.connection && this.connectionToken === token) {
      if (this.connection.state === HubConnectionState.Disconnected) {
        try {
          await this.connection.start();
          this.connected.set(true);
          this.refreshCount();
        } catch {
          this.connected.set(false);
        }
      }
      return;
    }

    await this.stop();
    this.connectionToken = token;

    const connection = new HubConnectionBuilder()
      .withUrl(this.hubUrl(), { accessTokenFactory: () => this.connectionToken ?? token })
      .withAutomaticReconnect([0, 2_000, 5_000, 10_000, 30_000])
      .configureLogging(LogLevel.Warning)
      .build();

    connection.on('notification', (item: AppNotification) => this.notificationSubject.next(item));
    connection.on('unreadCount', (count: number) => {
      if (Number.isFinite(count)) this.unreadCountSubject.next(count);
      // The per-application counts come from the same read; this keeps the sidebar's badges current too.
      this.refreshCount();
    });
    connection.onreconnecting(() => this.connected.set(false));
    connection.onreconnected(() => {
      this.connected.set(true);
      this.refreshCount();
    });
    connection.onclose(() => this.connected.set(false));

    this.connection = connection;

    try {
      await connection.start();
      // A newer sign-in may have replaced this connection while it was starting.
      if (this.connection !== connection) {
        await connection.stop();
        return;
      }
      this.connected.set(true);
      this.refreshCount();
    } catch {
      if (this.connection === connection) this.connected.set(false);
    }
  }

  private async stop(): Promise<void> {
    const connection = this.connection;
    this.connection = null;
    this.connectionToken = null;
    this.connected.set(false);

    if (connection) {
      try {
        await connection.stop();
      } catch {
        // Already closed.
      }
    }
  }

  /** The first count after connect/reconnect; the server pushes later changes. */
  private refreshCount(): void {
    this.notifications
      .unreadCount()
      .pipe(catchError(() => of(null)))
      .subscribe((count) => {
        if (count !== null) this.unreadCountSubject.next(count);
      });
  }

  private hubUrl(): string {
    const base = environment.apiBaseUrl.replace(/\/+$/, '');
    const root = base.endsWith('/api') ? base.slice(0, -4) : base;
    return `${root}/hubs/notifications`;
  }
}
